import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { useSelector } from "react-redux";

// import { SYS_NPS } from "../../services/journeyConfig.js";
import Card from "../card";
import { C, JCFG } from "../dashboard/_shared.jsx";
import {
  npsZoneAtual,
  npsScalePct,
  npsZoneLabels,
  jornadaProgressPct,
} from "./dataTransforms";
import { formatMeta, metaDefinida } from "../../utils/meta";
import "./style.scss";

export default function Overview({ per, metas }) {
  const overviewState = useSelector((state) => state.dashboard.overview);

  const data = overviewState?.data?.data?.[0];

  const cesConsolidado = data?.ces;
  const csatConsolidado = data?.csat;
  const cesTargetAvg = data?.ces_target;
  const csatTargetAvg = data?.csat_target;

  const cesUnimedSystem = data?.ces_unimed_system;
  const csatUnimedSystem = data?.csat_unimed_system;

  const jornadaCX = data?.cx;
  const jornadaMeta = metaDefinida(data?.cx_target);
  const jornadaSysAvg = data?.cx_unimed_system;

  const nps = data?.nps;
  const npsDelta = data?.nps_delta;
  const npsPromoterPercent = data?.nps_promoter_percent;
  const npsNeutralPercent = data?.nps_neutral_percent;
  const npsDetractorPercent = data?.nps_detractor_percent;

  const answers = data?.respondents;
  const npsTarget = data?.nps_target;
  const npsUnimedSystem = data?.nps_unimed_system;

  return (
    <Card
      style={{
        background: `linear-gradient(135deg,${C.g800} 0%,${C.g700} 100%)`,
        border: "none",
        padding: "20px 24px",
        marginBottom: 16,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div className="compOverview">
        <div className="decoCircle1" />
        <div className="decoCircle2" />
        <div className="mainRow">
          <div className="npsBlock">
            <div className="npsLabel">NPS GERAL</div>
            <div className="npsValueRow">
              <span className="npsScore">{nps}</span>
              <div className="npsDeltaCol">
                <div
                  className="npsDelta"
                  style={{
                    "--delta-color":
                      npsDelta > 0
                        ? "#4ade80"
                        : npsDelta < 0
                          ? "#f87171"
                          : "#FFFFFF",
                  }}
                >
                  {npsDelta > 0 && <ArrowUpRight size={14} />}
                  {npsDelta < 0 && <ArrowDownRight size={14} />}
                  {Math.abs(npsDelta)} pts
                </div>
                <div className="npsDeltaLabel">vs período anterior</div>
              </div>
            </div>
            <div className="npsMetaLabel">Meta {formatMeta(npsTarget)}</div>
          </div>
          <div className="scaleBlock">
            {/* NPS Scale */}
            {(() => {
              const pct = npsScalePct(nps);
              const zoneCurrent = npsZoneAtual(nps);
              const zoneLabels = npsZoneLabels();
              return (
                <div className="npsScaleWrap">
                  {/* Bar + pointer */}
                  <div className="npsBarWrap">
                    <div className="npsBar">
                      {/* Pointer circle sits on top of bar */}
                      <div
                        className="npsPointerWrap"
                        style={{ "--pointer-left": `${pct}%` }}
                      >
                        <div className="npsPointer" />
                      </div>
                    </div>
                    {/* Zone labels positioned absolutely below bar */}
                    <div className="npsZoneArea">
                      {/* Tick numbers */}
                      {[
                        [-100, 0],
                        [0, 50],
                        [50, 75],
                        [75, 87.5],
                        [100, 100],
                      ].map(([v, p]) => (
                        <span
                          key={v}
                          className="npsTick"
                          style={{ "--tick-left": `${p}%` }}
                        >
                          {v}
                        </span>
                      ))}
                      {/* Zone labels */}
                      {zoneLabels.map(({ l, r, c, left }) => {
                        const isActive = zoneCurrent === l;
                        return (
                          <div
                            key={l}
                            className={`npsZoneLabel ${isActive ? "is-active" : "is-inactive"}`}
                            style={{ "--zone-left": `${left}%` }}
                          >
                            <div
                              className="npsZoneDot"
                              style={{ "--zone-color": c }}
                            />
                            <div>
                              <div
                                className={`npsZoneName ${isActive ? "is-active" : "is-inactive"}`}
                              >
                                {l}
                              </div>
                              <div className="npsZoneRange">{r}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })()}
            {/* Detratores / Neutros / Promotores */}
            <div className="segmentBar">
              <div className="segmentBarTrack">
                <div
                  title={`Detratores (0-6): ${npsDetractorPercent}%`}
                  className="segmentBarSlice"
                  style={{
                    "--seg-width": `${npsDetractorPercent}%`,
                    "--seg-color": "#f87171",
                  }}
                />
                <div
                  title={`Neutros (7-8): ${npsNeutralPercent}%`}
                  className="segmentBarSlice"
                  style={{
                    "--seg-width": `${npsNeutralPercent}%`,
                    "--seg-color": "#fbbf24",
                  }}
                />
                <div
                  title={`Promotores (9-10): ${npsPromoterPercent}%`}
                  className="segmentBarSlice"
                  style={{
                    "--seg-width": `${npsPromoterPercent}%`,
                    "--seg-color": "#4ade80",
                  }}
                />
              </div>
              {/* Labels aligned to start of each segment */}
              <div className="segmentLabels">
                {[
                  {
                    label: "Detratores",
                    pct: npsDetractorPercent,
                    color: "#f87171",
                    range: "0–6",
                  },
                  {
                    label: "Neutros",
                    pct: npsNeutralPercent,
                    color: "#fbbf24",
                    range: "7–8",
                  },
                  {
                    label: "Promotores",
                    pct: npsPromoterPercent,
                    color: "#4ade80",
                    range: "9–10",
                  },
                ].map(({ label, pct, color, range }) => (
                  <div
                    key={label}
                    className="segmentLabelItem"
                    style={{ "--seg-width": `${pct}%` }}
                  >
                    <div className="segmentLabelInner">
                      <div
                        className="segmentDot"
                        style={{ "--seg-color": color }}
                      />
                      <div>
                        <div
                          className="segmentName"
                          style={{ "--seg-color": color }}
                        >
                          {label} <b>{pct}%</b>
                        </div>
                        <div className="segmentRange">{range}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="statsRow">
              {[
                ["Respondentes", answers],
                // ["Ondas", per.ondas],
                ["Meta", formatMeta(npsTarget)],
                ["Sist. Unimed", npsUnimedSystem],
              ].map(([l, v]) => (
                <div key={l} className="statItem">
                  <div className="statLabel">{l}</div>
                  <div className="statValue">{v}</div>
                </div>
              ))}
            </div>
          </div>
          {/* -- Consolidated KPIs -- */}
          <div className="kpiCol">
            {/* Jornada CX destaque */}
            <div className="jornadaCard">
              <div className="jornadaCardLabel">JORNADA CX</div>
              <div className="jornadaScore">{jornadaCX}%</div>
              <div className="jornadaMeta">
                {Object.keys(JCFG).length} etapas · Meta {formatMeta(jornadaMeta, "%")}
              </div>
              <div className="jornadaSys">
                Sist. Unimed <b>{jornadaSysAvg}%</b>
              </div>
              <div className="jornadaProgressTrack">
                <div
                  className="jornadaProgressFill"
                  style={{
                    "--progress-width": `${jornadaProgressPct(jornadaCX, jornadaMeta)}%`,
                  }}
                />
              </div>
            </div>
            {/* CES e CSAT médios */}
            <div className="kpiMiniRow">
              {[
                {
                  label: "CES MÉDIO",
                  value: cesConsolidado + "%",
                  meta: cesTargetAvg,
                  sys: cesUnimedSystem,
                  color: "#93c5fd",
                  sub: "5 etapas",
                },
                {
                  label: "CSAT MÉDIO",
                  value: csatConsolidado + "%",
                  meta: csatTargetAvg,
                  sys: csatUnimedSystem,
                  color: "#86efac",
                  sub: "6 etapas",
                },
              ].map((k, i) => (
                <div key={i} className="kpiMiniCard">
                  <div className="kpiMiniLabel">{k.label}</div>
                  <div
                    className="kpiMiniValue"
                    style={{ "--kpi-color": k.color }}
                  >
                    {k.value}
                  </div>
                  <div className="kpiMiniSub">
                    {k.sub} · Meta {formatMeta(k.meta, "%")}
                  </div>
                  <div className="kpiMiniSys">
                    Sist. Unimed <b>{k.sys}%</b>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
